import Vue from 'vue';
import Vuex from 'vuex';
import VuexPersist from 'vuex-persist';

import {
  ASK_QUESTION,
  GET_BUNDLES,
  GET_LOGIN_INFO,
  GET_STORES,
  HAS_ERROR,
  HAS_LOAN_APPLICATION_ERROR,
  IS_LOADING,
  NOTIFICATION,
  RESET_STORE,
  SET_BUNDLE_SELECTED_STORE_CODE,
  SET_BUNDLES,
  SET_CURRENT_STEP,
  SET_LANGUAGE,
  SET_LOAN_APPLICATION_DATA,
  SET_LOAN_APPLICATION_DECISION,
  SET_LOGIN_INFO,
  SET_SELECTED_BUNDLE,
  SET_SIGNATURE_STATUS,
  SET_STORES,
  SET_RENEWAL_ORDER,
  SET_RENEWABLE_PRODUCTS,
  SUBMIT_LOAN_APPLICATION,
  SET_LOAN_REFERENCE_NUMBER,
  SET_COOKIE_CONSENT,
  HANDLE_COOKIE_CONSENT,
  SET_GEOCODER_ERROR,
  POLL_PAYMENT_RESPONSE,
  HANDLE_PAYMENT_RESPONSE,
  SET_RESURS_DECISION_STATUS,
} from '@/types';
import HttpClient from '@/api/HttpClient';
import {
  COOKIE_CONSENT_LEVELS,
  PERSISTED_STORE_KEY,
  RESURS_DECISION_STATUSES,
  ROUTES,
  STEPS,
} from './constants';
import router from './router';
import i18n from './i18n';
import { getPersistedStore } from './utils';
import SubscriptionApi from '@/api/SubscriptionApi';
import {
  cookieRevision,
  getConsentObjectFromCookie,
  setCookie,
} from '@/utils/CookieUtil';

Vue.use(Vuex);

const vuexLocalStorage = new VuexPersist({
  key: PERSISTED_STORE_KEY,
  storage: window.localStorage,
  // Function that passes the state and returns the state with only the objects you want to store.
  reducer: (state) => ({
    language: state.language,
    currentStep: state.currentStep,
    selectedBundle: state.selectedBundle,
    loginInfo: state.loginInfo,
    loanApplicationData: state.loanApplicationData,
    loanApplicationDecision: state.loanApplicationDecision,
    loanReferenceNumber: state.loanReferenceNumber,
    signingData: state.signingData,
    storeCode: state.storeCode,
    renewableProducts: state.renewableProducts,
    decisionStatus: state.decisionStatus,
  }),
  // Function that passes a mutation and lets you decide if it should update the state in localStorage.
  // filter: mutation => (true)
});

const persistedStore = getPersistedStore();

export default new Vuex.Store({
  plugins: [vuexLocalStorage.plugin],
  state: {
    language: persistedStore.language || i18n.locale,
    currentStep: persistedStore.currentStep || STEPS.select,
    stores: [],
    bundles: [],
    selectedBundle: persistedStore.selectedBundle || null,
    loginInfo: null,
    loanApplicationData: persistedStore.loanApplicationData || null,
    isLoading: false,
    hasError: false,
    notification: null,
    hasLoanApplicationError: false,
    loanApplicationDecision: null,
    loanReferenceNumber: persistedStore.loanReferenceNumber || null,
    renewableOrder: null,
    renewableProducts: persistedStore.renewableProducts || null,
    consentInfo: getConsentObjectFromCookie(),
    signingData: null,
    signatureStatus: null,
    storeCode: persistedStore.storeCode || null,
    bundleOrders: {
      content: [],
    },
    geocoderError: null,
    retries: 0,
    maxRetries: 15,
    decisionStatus: persistedStore.decisionStatus || null,
  },
  actions: {
    [SET_LANGUAGE]({ commit }, language) {
      commit(SET_LANGUAGE, language);
    },
    [SET_COOKIE_CONSENT]({ commit }, consentLevels) {
      setCookie('cookie_consent', JSON.stringify(consentLevels), 365);
      commit(SET_COOKIE_CONSENT, {
        levels: consentLevels,
        revision: cookieRevision,
      });
    },
    [HANDLE_COOKIE_CONSENT]({ commit, state }) {
      if (state.consentInfo?.revision !== cookieRevision) {
        document.cookie =
          'cookie_consent= ; expires = Thu, 01 Jan 1970 00:00:00 GMT';
        commit(SET_COOKIE_CONSENT, null);
        return;
      }
      if (
        state.consentInfo?.levels?.includes(COOKIE_CONSENT_LEVELS.TARGETING)
      ) {
        window.fbq('consent', 'grant');
      } else {
        window.fbq('consent', 'revoke');
      }
      if (state.consentInfo?.levels?.includes(COOKIE_CONSENT_LEVELS.ANALYTIC)) {
        this._vm.$gtm?.enable(true);
        this._vm.$ga?.enable();
      } else {
        this._vm.$gtm?.enable(false);
        this._vm.$ga?.disable();
      }
    },
    async [GET_STORES]({ commit }) {
      try {
        const stores = await SubscriptionApi.getStores();

        commit(SET_STORES, stores);
      } catch (error) {
        commit(HAS_ERROR, error);
      }
    },
    async [GET_BUNDLES]({ commit }) {
      try {
        commit(IS_LOADING, true);

        const bundles = await SubscriptionApi.getBundles();

        commit(SET_BUNDLES, bundles);
      } catch (error) {
        commit(HAS_ERROR, error);
      } finally {
        commit(IS_LOADING, false);
      }
    },
    [SET_SELECTED_BUNDLE]({ commit }, bundle) {
      commit(SET_SELECTED_BUNDLE, bundle);
      commit(SET_CURRENT_STEP, bundle ? STEPS.apply : STEPS.select);
    },
    async [ASK_QUESTION]({ commit, state }, data) {
      try {
        commit(IS_LOADING, true);

        await SubscriptionApi.askQuestion({
          ...data,
          language: state.language,
        });
      } catch (error) {
        commit(HAS_ERROR, error);
        throw new Error(error);
      } finally {
        commit(IS_LOADING, false);
      }
    },
    async [GET_LOGIN_INFO]({ commit }) {
      try {
        commit(IS_LOADING, true);

        const response = await HttpClient.getLoginInfo();
        commit(SET_LOGIN_INFO, response);

        router.push({ name: ROUTES.LOAN_APPLICATION.name });
      } catch (error) {
        commit(HAS_ERROR, error);
        throw new Error(error);
      } finally {
        commit(IS_LOADING, false);
      }
    },
    [SET_LOAN_APPLICATION_DATA]({ commit }, data) {
      commit(SET_LOAN_APPLICATION_DATA, data);
    },
    async [SUBMIT_LOAN_APPLICATION]({ commit, state }) {
      try {
        commit(IS_LOADING, true);

        const response = await SubscriptionApi.submitLoanApplication(
          state.loanApplicationData,
        );

        commit(SET_LOAN_REFERENCE_NUMBER, response.referenceNumber);
        const paymentResponse = await SubscriptionApi.submitPayment(
          response.referenceNumber,
          state.language,
        );

        commit(SET_CURRENT_STEP, STEPS.paymentSubmitted);
        window.location.href = paymentResponse.redirectionUrl;
      } catch (error) {
        commit(HAS_LOAN_APPLICATION_ERROR, error);
      } finally {
        commit(IS_LOADING, false);
      }
    },
    async [POLL_PAYMENT_RESPONSE]({ state, dispatch, commit }) {
      state.retries = 0;
      try {
        const interval = setInterval(async () => {
          const response = await SubscriptionApi.pollPaymentResponse(
            state.loanReferenceNumber,
          );

          commit(SET_RESURS_DECISION_STATUS, response);

          if (state.retries >= state.maxRetries) {
            clearInterval(interval);
            commit(HAS_LOAN_APPLICATION_ERROR, true);
            return;
          } else if (response === RESURS_DECISION_STATUSES.RESERVED) {
            const renewableProducts =
              await SubscriptionApi.getRenewableProducts(
                state.loanReferenceNumber,
              );
            commit(SET_RENEWABLE_PRODUCTS, renewableProducts);
            clearInterval(interval);
            await router.push({ name: ROUTES.LOAN_APPLICATION_RENEW.name });
          } else if (response !== RESURS_DECISION_STATUSES.STARTED) {
            dispatch(HANDLE_PAYMENT_RESPONSE, response);
            clearInterval(interval);
          } else {
            state.retries++;
          }
        }, 5000);
      } catch (error) {
        commit(HAS_LOAN_APPLICATION_ERROR, error);
      }
    },
    async [HANDLE_PAYMENT_RESPONSE]({ state, commit }, response) {
      const { FROZEN, RESERVED, DECLINED } = RESURS_DECISION_STATUSES;

      switch (response) {
        case FROZEN:
          commit(IS_LOADING, false);
          await router.push({ name: ROUTES.LOAN_APPLICATION_INSPECTION.name });
          break;
        case RESERVED:
          commit(IS_LOADING, false);
          await router.push({ name: ROUTES.LOAN_APPLICATION_SUCCESS.name });
          break;
        case DECLINED:
          commit(IS_LOADING, false);
          await router.push({ name: ROUTES.LOAN_APPLICATION_REJECT.name });
          break;
        default:
          commit(IS_LOADING, false);
          await router.push({
            name: ROUTES.HOME.name,
            params: { productCode: state.productCode },
          });
      }
    },

    async [SET_RENEWAL_ORDER]({ state, commit }, product) {
      commit(IS_LOADING, true);
      const orderToLink = {
        newSubscriptionReference: state.loanReferenceNumber,
        renewableSubscriptionReference: product.subscriptionReferenceNumber,
      };
      await SubscriptionApi.linkSubscription(orderToLink);
      commit(SET_RESURS_DECISION_STATUS, null);
      commit(IS_LOADING, false);
      await router.push({ name: ROUTES.LOAN_APPLICATION_SUCCESS.name });
    },
  },

  mutations: {
    [RESET_STORE](state, isHalfSweep) {
      state.currentStep = STEPS.select;
      state.selectedBundle = null;
      state.loanReferenceNumber = null;
      state.loginInfo = null;
      state.loanApplicationData = null;
      state.loanApplicationDecision = isHalfSweep
        ? {
            referenceNumber: state.loanApplicationDecision.referenceNumber,
            accountNumber: state.loanApplicationDecision.accountNumber,
          }
        : null;
      state.storeCode = null;
      state.decisionStatus = null;
    },
    [SET_LANGUAGE](state, language) {
      state.language = language;
    },
    [SET_COOKIE_CONSENT](state, consentInfo) {
      state.consentInfo = consentInfo;
    },
    [IS_LOADING](state, loading) {
      state.isLoading = loading;
    },
    [HAS_ERROR](state, error) {
      state.hasError = error;
    },
    [NOTIFICATION](state, notification) {
      state.notification = notification;
    },
    [SET_CURRENT_STEP](state, step) {
      state.currentStep = step;
    },
    [SET_STORES](state, stores) {
      state.stores = stores;
    },
    [SET_BUNDLES](state, bundles) {
      state.bundles = bundles;
    },
    [SET_SELECTED_BUNDLE](state, bundle) {
      state.selectedBundle = bundle;
    },
    [SET_LOGIN_INFO](state, data) {
      state.loginInfo = data;
    },
    [SET_LOAN_APPLICATION_DATA](state, data) {
      state.loanApplicationData = data;
    },
    [HAS_LOAN_APPLICATION_ERROR](state, error) {
      state.hasLoanApplicationError = error;
    },
    [SET_LOAN_APPLICATION_DECISION](state, decision) {
      state.loanApplicationDecision = decision;
    },
    [SET_LOAN_REFERENCE_NUMBER](state, referenceNumber) {
      state.loanReferenceNumber = referenceNumber;
    },
    [SET_BUNDLE_SELECTED_STORE_CODE](state, storeCode) {
      state.storeCode = storeCode;
    },
    [SET_SIGNATURE_STATUS](state, status) {
      state.signatureStatus = status;
    },
    [SET_GEOCODER_ERROR](state, status) {
      state.geocoderError = status;
    },
    [SET_RENEWABLE_PRODUCTS](state, renewableProducts) {
      state.renewableProducts = renewableProducts;
    },
    [SET_RESURS_DECISION_STATUS](state, status) {
      state.decisionStatus = status;
    },
  },
  getters: {
    hasConsentByLevel: (state) => (consentLevel) =>
      state.consentInfo?.levels?.includes(consentLevel),
  },
});
